import type { TestResultElementValue } from '@vedalib/editor/lib/elements'
import { useMemo } from 'react'
import { chainName } from 'utils'

import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import type { IFieldConfig } from 'components/formKit'
import { FieldInput, FieldRadio, FieldSegmented, FieldSelect, FormField } from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import type { Block } from 'services/Store/Project/types'
import { t } from 'services/Translation'

import type { TestResultValueFormType } from './testResultTypes'

const useHiddenInfoFeedback = ({ name }: IFieldConfig<Block>) => {
  const value = useFormData<TestResultElementValue>(name, 1)
  return useMemo(() => ({ hidden: value.feedbackType === 'info' }), [value.feedbackType])
}

const useHiddenValidationFeedback = ({ name }: IFieldConfig<Block>) => {
  const value = useFormData<TestResultElementValue>(name, 1)
  return useMemo(() => ({ hidden: value.feedbackType === 'validation' }), [value.feedbackType])
}

const SUCCESS_TITLE = genElementValueField({
  as: FieldInput,
  name: 'resultSuccessTitle',
  label: t('elements.testResult.form.successTitle'),
  layout: 'vertical',
  props: { placeholder: t('input.placeholder.testSuccess') },
})

const FAIL_TITLE = genElementValueField({
  as: FieldInput,
  name: 'resultFailTitle',
  label: t('elements.testResult.form.failTitle'),
  layout: 'vertical',
  props: { placeholder: t('input.placeholder.testFail') },
})

const SHOW_DIAGRAM = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'showDiagram',
  label: t('elements.testResult.form.isDiagram'),
})

const POSITION = genElementValueField({
  as: FieldSelect<'top' | 'bottom' | 'left' | 'right'>,
  name: 'position',
  label: t('elements.testResult.form.position'),
  props: {
    fluid: true,
    options: [
      { value: 'top', label: t('input.option.top') },
      { value: 'bottom', label: t('input.option.bottom') },
      { value: 'left', label: t('input.option.left') },
      { value: 'right', label: t('input.option.right') },
    ],
  },
})

const SHOW_FEEDBACK = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'showFeedback',
  label: t('elements.testResult.form.isFeedback'),
})

const FEEDBACK_TYPE = genElementValueField({
  as: FieldRadio<'info' | 'validation'>,
  name: 'feedbackType',
  label: t('elements.testResult.form.feedback'),
  props: {
    options: [
      { value: 'info', label: t('input.option.commonFeedback') },
      { value: 'validation', label: t('input.option.dependentFeedback') },
    ],
  },
})

const FEEDBACK_SUCCESS = genElementValueField({
  as: FieldInput,
  name: 'resultFeedbackSuccess',
  label: t('elements.testResult.form.feedbackSuccess'),
  layout: 'vertical',
  props: { placeholder: t('input.placeholder.feedbackText') },
  useConfigs: [useHiddenInfoFeedback],
})

const FEEDBACK_FAIL = genElementValueField({
  as: FieldInput,
  name: 'resultFeedbackFail',
  label: t('elements.testResult.form.feedbackFail'),
  layout: 'vertical',
  props: { placeholder: t('input.placeholder.feedbackText') },
  useConfigs: [useHiddenInfoFeedback],
})

const FEEDBACK_COMMON = genElementValueField({
  as: FieldInput,
  name: 'resultFeedbackCommon',
  label: t('elements.testResult.form.feedbackCommon'),
  layout: 'vertical',
  props: { placeholder: t('input.placeholder.feedbackText') },
  useConfigs: [useHiddenValidationFeedback],
})

const TestResultForm: TestResultValueFormType = ({ name, element }) => (
  <Padding bottom gap sides>
    <FormField config={chainName(name, SUCCESS_TITLE)} />
    <FormField config={chainName(name, FAIL_TITLE)} />
    <FormField config={chainName(name, SHOW_DIAGRAM)} />
    {element.value.showDiagram && <FormField config={chainName(name, POSITION)} />}
    <FormField config={chainName(name, SHOW_FEEDBACK)} />
    {element.value.showFeedback && (
      <>
        <FormField config={chainName(name, FEEDBACK_TYPE)} />
        <FormField config={chainName(name, FEEDBACK_SUCCESS)} />
        <FormField config={chainName(name, FEEDBACK_FAIL)} />
        <FormField config={chainName(name, FEEDBACK_COMMON)} />
      </>
    )}
  </Padding>
)

export default TestResultForm
